import React from 'react';

// Outcome types for grants and applications
export type OutcomeType = 
  | 'Awaiting Decision'
  | 'Unsuccessful'
  | 'Awarded'
  | 'Approved';

interface OutcomeBadgeProps {
  outcome: OutcomeType; 
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'solid';
  className?: string;
}

// OutcomeBadge Component
export const OutcomeBadge: React.FC<OutcomeBadgeProps> = ({ 
  outcome, 
  size = 'md',
  variant = 'default',
  className = ''
}) => {
  const getOutcomeStyles = () => {
    const baseStyles = {
      'Awaiting Decision': { 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-700', 
        border: 'border-yellow-200', 
        solid: 'bg-yellow-500' 
      },
      'Unsuccessful': { 
        bg: 'bg-red-100', 
        text: 'text-red-700', 
        border: 'border-red-200', 
        solid: 'bg-red-500' 
      },
      'Awarded': { 
        bg: 'bg-blue-800', 
        text: 'text-blue-100', 
        border: 'border-blue-200', 
        solid: 'bg-amber-500' 
      },
      'Approved': { 
        bg: 'bg-emerald-100', 
        text: 'text-emerald-700', 
        border: 'border-emerald-200', 
        solid: 'bg-emerald-500' 
      },
    };

    return baseStyles[outcome] || baseStyles['Awaiting Decision'];
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-2 py-0.5 text-xs';
      case 'lg':
        return 'px-4 py-2 text-base';
      default:
        return 'px-3 py-1 text-sm';
    }
  };

  const getVariantStyles = () => {
    const colors = getOutcomeStyles();
    
    switch (variant) {
      case 'outlined':
        return `bg-transparent ${colors.text} border-2 ${colors.border}`;
      case 'solid':
        return `${colors.solid} text-white border-transparent`;
      default:
        return `${colors.bg} ${colors.text} border ${colors.border}`;
    }
  };

  return (
    <span 
      className={`inline-flex items-center rounded-full font-medium ${getSizeStyles()} ${getVariantStyles()} transition-all duration-200 hover:scale-105 ${className}`}
    >
      {outcome}
    </span>
  );
};

// Demo component
export default function OutcomeBadgeDemo() {
  const allOutcomes: OutcomeType[] = [
    'Awaiting Decision',
    'Unsuccessful',
    'Awarded',
    'Approved'
  ];

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold mb-8 text-gray-800">Outcome Badge Component</h1>
      
      <div className="space-y-8">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">All Outcomes (Default)</h2>
          <div className="flex flex-wrap gap-3">
            {allOutcomes.map(outcome => (
              <OutcomeBadge key={outcome} outcome={outcome} />
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Outlined Variant</h2>
          <div className="flex flex-wrap gap-3">
            {allOutcomes.map(outcome => (
              <OutcomeBadge key={outcome} outcome={outcome} variant="outlined" />
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Solid Variant</h2>
          <div className="flex flex-wrap gap-3">
            {allOutcomes.map(outcome => (
              <OutcomeBadge key={outcome} outcome={outcome} variant="solid" />
            ))}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Size Variations</h2>
          <div className="flex items-center gap-4">
            <OutcomeBadge outcome="Approved" size="sm" />
            <OutcomeBadge outcome="Approved" size="md" />
            <OutcomeBadge outcome="Approved" size="lg" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold mb-4 text-gray-700">Example Usage in Context</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-semibold">Research Grant 2024</p>
                <p className="text-sm text-gray-600">Application ID: #12345</p>
              </div>
              <OutcomeBadge outcome="Awarded" />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-semibold">Innovation Fund</p>
                <p className="text-sm text-gray-600">Application ID: #12346</p>
              </div>
              <OutcomeBadge outcome="Awaiting Decision" />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-semibold">Community Project Grant</p>
                <p className="text-sm text-gray-600">Application ID: #12347</p>
              </div>
              <OutcomeBadge outcome="Approved" />
            </div>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-semibold">Development Grant</p>
                <p className="text-sm text-gray-600">Application ID: #12348</p>
              </div>
              <OutcomeBadge outcome="Unsuccessful" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}